import styled from "@emotion/styled"
import { InfoWindow } from "@vis.gl/react-google-maps"
import { PointsOfInterestDto } from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { trackLocationInfo } from "reusable/gtm"
import { extractProperty, getStationSpacesForRent } from "utils"

type PazCenterInfoWindowProps = {
    position: { lat: number; lng: number }
    onClose: () => void
    item: PointsOfInterestDto | null
    umbracoChildren?: any[]
    onMoreInfoClick?: () => void
    expandedOpen?: boolean
    moreInfoButtonRef?: React.RefObject<HTMLButtonElement>
    mode?: 'rent' | 'sell'
    station?: {
        landSize?: string
    }
}



const StyledTitle = styled.div`
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 16px;
`

const StyledContent = styled.div`
  margin-bottom: 8px;
`

const StyledSection = styled.div`
  margin-bottom: 6px;
  &:first-child {
    border-bottom: 1px solid #fff;
  }
  &:last-child {
    margin-bottom: 0;
  }
`

const StyledAdditionalInfo = styled.button`
    justify-content: flex-end;
    padding-top: 6px;
    margin-top: 6px;
    font-size: 18px;
    font-weight: bold;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: opacity 0.2s ease;
    background: none;
    border: none;
    color: inherit;
    padding-inline: 0;
    box-shadow: none;
    width:100%;

    &:hover, &:focus-visible {
        opacity: 0.8;
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`

function PazCenterInfoWindow({ position, onClose, item, umbracoChildren, onMoreInfoClick, expandedOpen, moreInfoButtonRef, mode = 'rent', station }: PazCenterInfoWindowProps) {
    const t = useTranslations('pazCenter')
    if (!item) { return null }

    const shortName = item.ShortName || item.Name
    const address = [item.Street, item.CityName].filter(Boolean).join(', ') || t('results.noAddress')
    const stationId = item.Id?.toString()
    const umbracoStation = umbracoChildren?.filter((child: any) => {
        const childStationId = extractProperty(child, "satiationId")?.toString?.()
        return childStationId === stationId
    })?.[0]

    const stationSpaces = mode === 'rent' ? getStationSpacesForRent(umbracoStation) : []
    // Determine if this is a planned site (TypeId 9)
    const isPlanned = item.PointTypes && item.PointTypes[0]?.TypeId === 9
    const spaceIcon = isPlanned ? "/icons/spaceBlack.svg" : "/icons/space.svg"
    const handleMoreInfoClick = () => {
        if (onMoreInfoClick) {
            onMoreInfoClick()
        }
        trackLocationInfo(shortName, address)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        const target = e.target as HTMLElement
        if (e.key === 'Tab' && e.shiftKey) {
            if (target.tagName === 'BUTTON') {

                e.preventDefault()
                // Focus back to the region container
                const region = document.getElementById('pazcenter-infowindow-focus')
                setTimeout(() => {
                    region?.focus()
                }, 0)
                return
            }
            const tabButton = document.getElementById(`pazcenter-tab-${item.Id}`)
            if (tabButton) {
                e.preventDefault()
                tabButton.focus()
            }
        }
    }

    return (
        <InfoWindow
            position={position}
            onCloseClick={onClose}
            shouldFocus={false}
        >
            <div
                id="pazcenter-infowindow-focus"
                tabIndex={0}
                aria-labelledby="pazcenter-infowindow-title"
                role="region"
                aria-live="polite"
                onKeyDown={handleKeyDown}
            >
                <StyledTitle id="pazcenter-infowindow-title">
                    {shortName}
                </StyledTitle>

                <StyledContent>
                    <StyledSection>
                        <div style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '400' }}>
                            {address}
                        </div>
                    </StyledSection>
                    {mode === 'rent' && stationSpaces.length > 0 && (
                        <StyledSection>
                            <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px' }}>
                                {t('space.availableForRent')}:
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '400' }}>
                                {stationSpaces.map((space: string, index: number) => (
                                    <div key={index} style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                        <Image alt="" width={16} height={16} src={spaceIcon} />
                                        {space} מ״ר
                                    </div>
                                ))}
                            </div>

                        </StyledSection>
                    )}
                    {mode === 'sell' && station?.landSize && (
                        <StyledSection>
                            <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px' }}>
                                שטח החלקה:
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '400' }}>
                                <div style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                    <Image alt="" width={16} height={16} src={spaceIcon} />
                                    {station.landSize}
                                </div>
                            </div>
                        </StyledSection>
                    )}
                </StyledContent>


                <StyledAdditionalInfo
                    ref={moreInfoButtonRef}
                    type="button"
                    onClick={handleMoreInfoClick}
                    aria-label={`${t('actions.moreInfo')} ${shortName}`}
                    aria-expanded={!!expandedOpen}
                    aria-controls={`pazcenter-panel-${item.Id}`}
                    autoFocus={false}
                >
                    {t('actions.moreInfo')} <Image style={{ objectFit: "cover" }} alt="" width={18} height={9} src="/icons/arrowIcon.svg" />
                </StyledAdditionalInfo>
            </div>
        </InfoWindow >
    )
}
export default PazCenterInfoWindow
